import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { forwardRef } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { KimUploadedFilesList } from './kim-uploaded-files-list';
import { KimUploadedFile } from '../hooks/use-kim-file-upload';
import {
  KIM_ALLOWED_FILE_TYPES,
  KIM_DROPZONE_ACCEPT,
  KIM_MAX_FILE_SIZE,
  KIM_MAX_NUMBER_OF_FILES,
} from '../utils/upload-constants';
import { DocMentionsInput, DocMentionsInputHandle } from '@/new/components/doc-mentions-input';
import { Icon } from '@/new/components/icon';
import { InfoCard } from '@/new/components/info-card';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

const SAVE_AS_TEMPLATE_MIN_LENGTH = 100;

interface KimInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onInterrupt?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** Blocks submitting (button + Enter) without disabling typing. */
  sendDisabled?: boolean;
  /** An interrupt has been requested but the run hasn't ended yet. */
  isInterrupting?: boolean;
  uploadedFiles?: KimUploadedFile[];
  isUploading?: boolean;
  isGenerating?: boolean;
  onFilesAdded?: (files: File[]) => void;
  onFileRemove?: (fileId: string) => void;
  onSaveAsTemplate?: () => void;
  onFocus?: () => void;
  autoFocus?: boolean;
  matterId?: string;
  subsetId?: string;
}

export const KimInput = forwardRef<DocMentionsInputHandle, KimInputProps>(function KimInput(
  {
    value,
    onChange,
    onSubmit,
    onInterrupt,
    placeholder,
    className,
    disabled,
    sendDisabled,
    isInterrupting,
    uploadedFiles = [],
    isUploading = false,
    isGenerating,
    onFilesAdded,
    onFileRemove,
    onSaveAsTemplate,
    onFocus,
    autoFocus,
    matterId: matterIdProp,
    subsetId: subsetIdProp,
  },
  ref,
) {
  const params = useParams();
  const matterId = matterIdProp ?? params.matterId;
  const subsetId = subsetIdProp ?? params.subsetId;
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<DocMentionsInputHandle>(null);

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    triggerMention: () => inputRef.current?.triggerMention(),
  }));

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        onFilesAdded?.(acceptedFiles);
      }
    },
    [onFilesAdded],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
    accept: KIM_DROPZONE_ACCEPT,
    maxSize: KIM_MAX_FILE_SIZE,
    maxFiles: KIM_MAX_NUMBER_OF_FILES,
    onDropRejected: (rejections) => {
      for (const rejection of rejections) {
        for (const error of rejection.errors) {
          if (error.code === 'file-too-large') {
            toast.error(`File is too large. Maximum size is ${KIM_MAX_FILE_SIZE / 1024 / 1024}MB.`);
          } else if (error.code === 'too-many-files') {
            toast.error(`Too many files. Maximum is ${KIM_MAX_NUMBER_OF_FILES} files.`);
          } else if (error.code === 'file-invalid-type') {
            toast.error(`Unsupported file type. Allowed: ${KIM_ALLOWED_FILE_TYPES.join(', ')}.`);
          } else {
            toast.error(error.message);
          }
        }
      }
    },
  });

  const [hasUnfilledPlaceholders, setHasUnfilledPlaceholders] = useState(false);

  if (!matterId) return null;

  const isInputDisabled = disabled || !subsetId;
  const isSubmitDisabled = disabled || sendDisabled || isUploading || hasUnfilledPlaceholders;

  // Info hint when a submit is attempted while sending is blocked
  const [showSendBlockedHint, setShowSendBlockedHint] = useState(false);
  const hintTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(hintTimeoutRef.current), []);

  // Hide the hint as soon as sending unlocks
  useEffect(() => {
    if (!sendDisabled) setShowSendBlockedHint(false);
  }, [sendDisabled]);

  const showHint = useCallback(() => {
    setShowSendBlockedHint(true);
    clearTimeout(hintTimeoutRef.current);
    hintTimeoutRef.current = setTimeout(() => setShowSendBlockedHint(false), 3000);
  }, []);

  const handleAttemptSubmit = useCallback(() => {
    if (isSubmitDisabled) {
      if (!disabled && sendDisabled) showHint();
      return;
    }
    onSubmit();
  }, [isSubmitDisabled, disabled, sendDisabled, showHint, onSubmit]);

  return (
    <div
      {...getRootProps()}
      className={cn('relative w-full', isDragActive && 'rounded-lg ring-2 ring-blue-400 ring-offset-1')}
    >
      <input {...getInputProps()} />

      <AnimatePresence>
        {showSendBlockedHint && (
          <motion.div
            key="send-blocked-hint"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.15 }}
            className="absolute -top-12 right-2 z-10"
          >
            <InfoCard className="text-xs">You can send your next message once Kim has finished responding.</InfoCard>
          </motion.div>
        )}
      </AnimatePresence>

      <div
        ref={containerRef}
        onClick={() => inputRef.current?.focus()}
        className={cn(
          'w-full cursor-text rounded-xl border bg-white text-sm ring-offset-2 ring-offset-transparent transition-colors duration-150 focus-within:ring-1 focus-within:ring-teal-500 focus-within:ring-offset-white',
          isInputDisabled && 'border-gray-200/60 bg-[#fcfcfc]',
        )}
      >
        <div className="mx-2">
          <KimUploadedFilesList uploadedFiles={uploadedFiles} isUploading={isUploading} onFileRemove={onFileRemove} />
        </div>

        <DocMentionsInput
          ref={inputRef}
          value={value}
          onChange={onChange}
          onSubmit={handleAttemptSubmit}
          onEscape={isGenerating ? onInterrupt : undefined}
          onPlaceholdersChange={setHasUnfilledPlaceholders}
          onFocus={onFocus}
          matterId={matterId}
          subsetId={subsetId ?? ''}
          placeholder={placeholder}
          className={className}
          disabled={isInputDisabled}
          autoFocus={autoFocus}
          popoverAnchorRef={containerRef}
        />

        <div className={cn('flex items-end p-2 pt-0', isInputDisabled && 'opacity-50')}>
          <AnimatePresence initial={false}>
            {onSaveAsTemplate && value.length >= SAVE_AS_TEMPLATE_MIN_LENGTH && (
              <motion.div
                key="save-as-template"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <Button variant="ghost" size="sm" onClick={onSaveAsTemplate} className="m-1">
                  <Icon className="lucide--layout-template" /> Save prompt as template
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* Kept clickable even when submit is blocked (aria-disabled, not disabled) so
                  handleAttemptSubmit can show the "wait for Kim" hint and the tooltip still
                  fires on hover. A real disabled button swallows both. */}
              <Button
                aria-disabled={!isGenerating && isSubmitDisabled}
                onClick={isGenerating ? onInterrupt : handleAttemptSubmit}
                variant={isGenerating || isInterrupting ? 'default' : 'primary'}
                className={cn(
                  'm-1 ml-auto size-8 rounded-full',
                  !isGenerating && isSubmitDisabled && 'cursor-not-allowed opacity-50',
                )}
              >
                {isGenerating ? (
                  <Icon className="lucide--square" />
                ) : isInterrupting ? (
                  <Icon className="lucide--loader animate-spin" />
                ) : (
                  <Icon className="lucide--arrow-right" />
                )}
              </Button>
            </TooltipTrigger>
            {hasUnfilledPlaceholders ? (
              <TooltipContent side="top">Fill in remaining placeholders before submitting a query</TooltipContent>
            ) : (
              isGenerating && (
                <TooltipContent side="top" className="px-1.5">
                  <kbd className="mr-1">Esc</kbd>
                  to interrupt your last message
                </TooltipContent>
              )
            )}
          </Tooltip>
        </div>
      </div>
    </div>
  );
});
